import { memo, useRef } from 'react'
import { ArrowDown, ArrowUp, FolderOpen, GripVertical, Plus, Trash2 } from 'lucide-react'
import type { MusicTrack } from '../../lib/infoserviceSlideshowApi.js'
import { formatClock, formatSpan } from '../../lib/slideshowSettingsForm.js'
import { Spinner } from '../common/Spinner.js'

interface MusicPlaylistProps {
  tracks: MusicTrack[]
  totalMs: number
  /** Nom du fichier en cours d'import (normalisation comprise), sinon `null`. */
  uploading: string | null
  disabled: boolean
  /** Titre du morceau en cours de reprise depuis le disque, sinon `null`. */
  restoring: string | null
  /** Ouvre le sélecteur de fichiers sur INFOSERVICES/MUSIQUE. */
  onPickExisting: () => void
  onUpload: (files: File[]) => void
  onMove: (trackId: string, delta: -1 | 1) => void
  onRemove: (track: MusicTrack) => void
}

function loudnessText(track: MusicTrack): string {
  if (track.loudnessLufs !== null) return `${track.loudnessLufs.toLocaleString('fr-FR')} → −23 LUFS`
  return track.normalized ? '−23 LUFS' : '—'
}

/**
 * Playlist en ordre fixe. La lecture continue d'une plage à l'autre : la plage
 * suivante reprend où la précédente s'est arrêtée, avec un fondu d'ouverture.
 * Un morceau retiré reste dans le dossier musique : le sélecteur de fichiers
 * le retrouve, sans nouvel import.
 */
export const MusicPlaylist = memo(function MusicPlaylist({
  tracks, totalMs, uploading, disabled, restoring, onPickExisting, onUpload, onMove, onRemove,
}: MusicPlaylistProps) {
  const input = useRef<HTMLInputElement>(null)
  const working = uploading !== null || restoring !== null
  return (
    <div className="slideshow-playlist">
      <div className="slideshow-playlist__head">
        <span className="slideshow-muted">Ordre fixe — la lecture reprend d’une plage à l’autre</span>
        <span className="slideshow-pill">{tracks.length} morceau{tracks.length > 1 ? 'x' : ''}{tracks.length ? ` · ${formatSpan(totalMs)}` : ''}</span>
      </div>

      {tracks.length === 0 && !uploading && (
        <p className="slideshow-playlist__empty">Aucun morceau. Ajoutez vos fichiers : ils sont normalisés à −23 LUFS à l’import.</p>
      )}

      {tracks.length > 0 && (
        <ol className="slideshow-playlist__list">
          {tracks.map((track, index) => (
            <li key={track.id} className="slideshow-playlist__track">
              <GripVertical size={14} className="slideshow-muted" aria-hidden="true" />
              <span className="slideshow-playlist__index">{String(index + 1).padStart(2, '0')}</span>
              <span className="slideshow-playlist__title" title={track.sourcePath}>{track.title}</span>
              <span className="slideshow-playlist__loudness" title="Sonie mesurée sur le fichier d’origine, ramenée à −23 LUFS">
                {loudnessText(track)}
              </span>
              <span className="slideshow-mono">{formatClock(track.durationMs)}</span>
              <span className="slideshow-playlist__actions">
                <button type="button" className="tp-icon-button" disabled={disabled || index === 0} onClick={() => onMove(track.id, -1)} aria-label={`Monter ${track.title}`}>
                  <ArrowUp size={14} aria-hidden="true" />
                </button>
                <button type="button" className="tp-icon-button" disabled={disabled || index === tracks.length - 1} onClick={() => onMove(track.id, 1)} aria-label={`Descendre ${track.title}`}>
                  <ArrowDown size={14} aria-hidden="true" />
                </button>
                <button type="button" className="tp-icon-button" disabled={disabled} onClick={() => onRemove(track)} aria-label={`Retirer ${track.title} de la playlist`}>
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}

      {uploading && (
        <p className="slideshow-playlist__uploading" role="status"><Spinner /> Import et normalisation de « {uploading} »…</p>
      )}
      {restoring && (
        <p className="slideshow-playlist__uploading" role="status"><Spinner /> Ajout de « {restoring} »…</p>
      )}

      <input
        ref={input}
        type="file"
        accept="audio/*,.wav,.mp3,.flac,.aac,.m4a,.ogg,.aif,.aiff"
        multiple
        hidden
        onChange={event => {
          const files = [...(event.target.files ?? [])]
          event.target.value = ''
          if (files.length) onUpload(files)
        }}
      />
      <div className="slideshow-playlist__add">
        <button type="button" className="tp-button" disabled={disabled || working} onClick={() => input.current?.click()}>
          <Plus size={14} aria-hidden="true" /> Ajouter des morceaux
        </button>
        <button type="button" className="tp-button" disabled={disabled || working} onClick={onPickExisting}>
          <FolderOpen size={14} aria-hidden="true" /> Reprendre un morceau existant
        </button>
      </div>
    </div>
  )
})
